type Variant = 'light' | 'dark' | 'on-gradient';

interface FinLabLogoProps {
  /** `light` — dark wordmark + purple bars on white surfaces.
   *  `dark` — white wordmark + purple bars on gray-900 surfaces (sidebar).
   *  `on-gradient` — white wordmark + white bars on the purple→blue auth gradient. */
  variant?: Variant;
  size?: 'sm' | 'md' | 'lg';
  /** Render only the glyph (no wordmark). */
  glyphOnly?: boolean;
  className?: string;
}

const SIZES = {
  sm: { gap: 14, mid: 22, tall: 30, font: 'text-[18px]', barWidth: 5, barGap: 2 },
  md: { gap: 14, mid: 22, tall: 30, font: 'text-[22px]', barWidth: 5, barGap: 2 },
  lg: { gap: 18, mid: 28, tall: 38, font: 'text-[28px]', barWidth: 6, barGap: 3 },
} as const;

/**
 * FinLab brand mark — bar-chart glyph + wordmark.
 * Per design system: never recolor outside purple-600 / gray-900 / white,
 * never outline, never use a different typeface.
 */
export function FinLabLogo({
  variant = 'light',
  size = 'md',
  glyphOnly = false,
  className = '',
}: FinLabLogoProps) {
  const { gap, mid, tall, font, barWidth, barGap } = SIZES[size];

  let barColors: [string, string, string];
  let wordmarkTone: string;

  if (variant === 'on-gradient') {
    barColors = ['rgba(255,255,255,0.6)', 'rgba(255,255,255,0.78)', 'rgba(255,255,255,0.95)'];
    wordmarkTone = 'text-white';
  } else if (variant === 'dark') {
    barColors = ['#c4b5fd', '#a78bfa', '#8b5cf6'];
    wordmarkTone = 'text-white';
  } else {
    barColors = ['#a78bfa', '#8b5cf6', '#7c3aed'];
    wordmarkTone = 'text-gray-900';
  }

  return (
    <span className={`inline-flex items-center gap-2.5 leading-none ${className}`}>
      <span className="inline-flex items-end" style={{ gap: barGap }} aria-hidden>
        <span style={{ width: barWidth, height: gap, borderRadius: 2, background: barColors[0] }} />
        <span style={{ width: barWidth, height: mid, borderRadius: 2, background: barColors[1] }} />
        <span style={{ width: barWidth, height: tall, borderRadius: 2, background: barColors[2] }} />
      </span>
      {!glyphOnly && (
        <span className={`font-black tracking-[-0.04em] ${font} ${wordmarkTone}`}>
          FinLab
        </span>
      )}
    </span>
  );
}
